import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePropertyId } from '../../common/hooks/usePropertyId';
import { useRoomsData } from '../../common/hooks/useRoomsData';
import { usePaymentsData } from '../../common/hooks/usePaymentsData';
import { RESERVATION_STATUS } from '../../common/enums';
import BookingStepper, { BOOKING_PAYMENT_STEP_INDEX } from './components/BookingStepper';
import ReservationSummary from './components/reservation/ReservationSummary';
import ReservationCustomerDetails from './components/reservation/ReservationCustomerDetails';
import ReservationPayment from './components/reservation/ReservationPayment';
import { getCheckoutErrorMessage } from './components/reservation/reservationUtils';
import { withMultipleQueryData } from '../../common/withQueryData';

const ReservationCheckoutPageContent = ({ queryResults, reservation }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const room = queryResults.room.data;
  const payments = queryResults.payments.data ?? [];

  const checkoutErrorCode = searchParams.get('error');
  const [bannerError, setBannerError] = useState(() => getCheckoutErrorMessage(checkoutErrorCode));

  useEffect(() => {
    if (checkoutErrorCode) {
      return;
    }

    if (reservation?.status && reservation.status !== RESERVATION_STATUS.DRAFT) {
      navigate(`/reservation/${reservation.id}`, { replace: true });
    }
  }, [reservation, navigate, checkoutErrorCode]);

  useEffect(() => {
    if (!checkoutErrorCode) return;

    const message = getCheckoutErrorMessage(checkoutErrorCode);
    if (message) {
      setBannerError(message);
    }
    setSearchParams({}, { replace: true });
  }, [checkoutErrorCode, setSearchParams]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <Container sx={{ py: 8, maxWidth: 'lg' }}>
        <Box sx={{ width: '100%' }}>
          <BookingStepper activeStep={BOOKING_PAYMENT_STEP_INDEX} />

          <Button
            startIcon={<ArrowBack />}
            sx={{ mb: 2, visibility: 'hidden', pointerEvents: 'none' }}
            tabIndex={-1}
            aria-hidden
          >
            Wstecz
          </Button>

          {bannerError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBannerError(null)}>
              {bannerError}
            </Alert>
          )}

          <ReservationSummary order={reservation} roomName={room?.name} />
          <ReservationCustomerDetails order={reservation} />
          <ReservationPayment
            reservation={reservation}
            payments={payments}
          />
        </Box>
      </Container>
    </Box>
  );
};

const ReservationCheckoutPageContentWithQueryData = withMultipleQueryData(ReservationCheckoutPageContent);

const ReservationCheckoutPage = ({ reservation }) => {
  const propertyId = usePropertyId();
  const { queryResults: roomQuery } = useRoomsData(propertyId, reservation.roomId);
  const { queryResults: paymentsQuery } = usePaymentsData(propertyId, reservation.id);

  return (
    <ReservationCheckoutPageContentWithQueryData
      reservation={reservation}
      queryResults={{
        room: roomQuery,
        payments: paymentsQuery,
      }}
    />
  );
};

export default ReservationCheckoutPage;
